import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { useAuth } from './AuthContext'

const GroupContext = createContext({})

export function GroupProvider({ children }) {
  const { user } = useAuth()
  const [groups, setGroups] = useState([])          // all groups user belongs to
  const [activeGroup, setActiveGroup] = useState(null)  // currently selected group object
  const [loadingGroups, setLoadingGroups] = useState(true)

  useEffect(() => {
    if (user) {
      loadGroups()
    } else {
      setGroups([])
      setActiveGroup(null)
      setLoadingGroups(false)
    }
  }, [user])

  async function loadGroups(switchToId) {
    setLoadingGroups(true)
    const { data: memberships } = await supabase
      .from('group_members')
      .select('*, groups(*)')
      .eq('user_id', user.id)
      .order('joined_at', { ascending: true })

    const allGroups = (memberships || []).map(m => m.groups).filter(Boolean)
    setGroups(allGroups)

    if (allGroups.length > 0) {
      if (switchToId) {
        // Explicitly switch to a specific group (e.g. after create/join)
        const target = allGroups.find(g => g.id === switchToId)
        if (target) {
          setActiveGroup(target)
          localStorage.setItem(`fp_active_group_${user.id}`, switchToId)
        } else {
          setActiveGroup(allGroups[0])
        }
      } else {
        // Restore last-used group from localStorage, or default to first
        const savedId = localStorage.getItem(`fp_active_group_${user.id}`)
        const saved = allGroups.find(g => g.id === savedId)
        setActiveGroup(saved || allGroups[0])
      }
    } else {
      setActiveGroup(null)
    }

    setLoadingGroups(false)
  }

  function switchGroup(groupId) {
    const found = groups.find(g => g.id === groupId)
    if (found) {
      setActiveGroup(found)
      localStorage.setItem(`fp_active_group_${user.id}`, groupId)
    }
  }

  // Called after creating or joining a group to refresh the list
  // Pass groupId to auto-switch to that group
  async function refreshGroups(switchToId) {
    await loadGroups(switchToId)
  }

  return (
    <GroupContext.Provider value={{
      groups,
      activeGroup,
      loadingGroups,
      switchGroup,
      refreshGroups,
    }}>
      {children}
    </GroupContext.Provider>
  )
}

export const useGroup = () => useContext(GroupContext)
